import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import Button from "components/Button";
import Modal from "components/Modal";
import color from "constants/color";
import font from "constants/font";
import { useFindLatestAiSoftwareVersions } from "pages/AimoParking/AiSoftware/api";
import AimoParkingSelect from "pages/AimoParking/component/AimoParkingSelect";
import { useUpdateEdgeDeviceAiVersion } from "pages/AimoParking/EdgeDevice/api";

const REQUEST_INTERVAL_MS = 500;

type ItemStatus = "pending" | "running" | "success" | "failed";
type ItemState = { status: ItemStatus; errorMessage?: string };
type Phase = "select" | "confirm" | "running" | "done";
type ChoiceType = { key?: number | string; label: string; value: string; disabled?: boolean };

const Body = styled.div`
  width: 640px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background-color: ${color.white};
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${color.border};
  font-size: ${font.size20};
  font-weight: bold;
`;

const Content = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${color.border};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background-color: ${color.background};
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const FormLabel = styled.div`
  min-width: 160px;
  font-size: ${font.size14};
  color: ${color.text.gray};
`;

const FormValue = styled.div`
  font-size: ${font.size16};
`;

const SummaryLine = styled.div`
  font-size: ${font.size14};
  margin-bottom: 8px;
`;

const ProgressBarWrap = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background-color: ${color.background};
  border: 1px solid ${color.border};
  overflow: hidden;
  margin: 8px 0 16px;
`;

const ProgressBarFill = styled.div<{ ratio: number }>`
  width: ${(props) => Math.min(100, Math.max(0, props.ratio * 100))}%;
  height: 100%;
  background-color: ${color.text.link};
  transition: width 0.2s ease;
`;

const Counts = styled.div`
  display: flex;
  gap: 16px;
  font-size: ${font.size14};
  margin-bottom: 12px;
`;

const Pill = styled.span<{ tone: "neutral" | "running" | "success" | "failed" }>`
  display: inline-block;
  min-width: 64px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: ${font.size12};
  text-align: center;
  color: ${color.white};
  background-color: ${(props) =>
    props.tone === "success"
      ? color.green
      : props.tone === "failed"
      ? color.warning
      : props.tone === "running"
      ? color.text.link
      : color.text.gray};
`;

const ItemList = styled.div`
  border: 1px solid ${color.border};
  border-radius: 4px;
  max-height: 320px;
  overflow-y: auto;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid ${color.border};
  font-size: ${font.size14};
  &:last-child {
    border-bottom: none;
  }
`;

const ItemSerial = styled.div`
  flex: 1;
  font-weight: 500;
`;

const ItemError = styled.div`
  flex: 2;
  color: ${color.warning};
  font-size: ${font.size12};
`;

const labelForStatus = (s: ItemStatus): string => {
  switch (s) {
    case "pending":
      return "待機中";
    case "running":
      return "実行中";
    case "success":
      return "成功";
    case "failed":
      return "失敗";
  }
};

const toneForStatus = (s: ItemStatus): "neutral" | "running" | "success" | "failed" => {
  switch (s) {
    case "pending":
      return "neutral";
    case "running":
      return "running";
    case "success":
      return "success";
    case "failed":
      return "failed";
  }
};

const extractErrorMessage = (e: unknown): string => {
  if (e == null) return "不明なエラー";
  if (typeof e === "string") return e;
  if (typeof e === "object") {
    const obj = e as { data?: { message?: string }; status?: number; statusText?: string; message?: string };
    if (obj.data?.message) return obj.data.message;
    if (obj.status) return `HTTP ${obj.status}${obj.statusText ? ` ${obj.statusText}` : ""}`;
    if (obj.message) return obj.message;
  }
  return "通信エラー";
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const BulkUpdateAiVersionModal = ({
  isVisible,
  selectedDevices,
  onClose,
  onAllSucceeded,
}: {
  isVisible: boolean;
  selectedDevices: AimoParkingEdgeDeviceSummary[];
  onClose: () => void;
  onAllSucceeded: (updatedIds: string[]) => void;
}) => {
  const { data: latestAiSoftwares } = useFindLatestAiSoftwareVersions();
  const { updateAiVersion } = useUpdateEdgeDeviceAiVersion();

  const [phase, setPhase] = useState<Phase>("select");
  const [selectedVersion, setSelectedVersion] = useState<ChoiceType | null>(null);
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({});

  const aiVersionChoices: ChoiceType[] = useMemo(() => {
    const list = Array.isArray(latestAiSoftwares)
      ? latestAiSoftwares
      : latestAiSoftwares != null
      ? [latestAiSoftwares as AimoParkingAiSoftwareResponse]
      : [];
    return list.map((item) => ({ label: item.version, value: item.id }));
  }, [latestAiSoftwares]);

  const counts = useMemo(() => {
    const totals = { pending: 0, running: 0, success: 0, failed: 0 };
    for (const device of selectedDevices) {
      const state = itemStates[device.id]?.status ?? "pending";
      totals[state] += 1;
    }
    return totals;
  }, [itemStates, selectedDevices]);

  const completedCount = counts.success + counts.failed;
  const totalCount = selectedDevices.length;

  const runUpdates = useCallback(
    async (targets: AimoParkingEdgeDeviceSummary[]) => {
      if (selectedVersion == null) return;
      setItemStates((prev) => {
        const next = { ...prev };
        for (const t of targets) next[t.id] = { status: "pending" };
        return next;
      });
      setPhase("running");

      for (let i = 0; i < targets.length; i++) {
        const device = targets[i];
        const startedAt = Date.now();
        setItemStates((prev) => ({ ...prev, [device.id]: { status: "running" } }));
        try {
          const response = await updateAiVersion({
            edgeDeviceId: device.id,
            aiSoftwareId: selectedVersion.value,
          });
          if (response.status === 200) {
            setItemStates((prev) => ({ ...prev, [device.id]: { status: "success" } }));
          } else {
            setItemStates((prev) => ({
              ...prev,
              [device.id]: { status: "failed", errorMessage: `HTTP ${response.status}` },
            }));
          }
        } catch (e) {
          setItemStates((prev) => ({
            ...prev,
            [device.id]: { status: "failed", errorMessage: extractErrorMessage(e) },
          }));
        }
        if (i < targets.length - 1) {
          const elapsed = Date.now() - startedAt;
          const wait = REQUEST_INTERVAL_MS - elapsed;
          if (wait > 0) await sleep(wait);
        }
      }

      setPhase("done");
    },
    [selectedVersion, updateAiVersion]
  );

  const handleStart = useCallback(() => {
    setPhase("confirm");
  }, []);

  const handleConfirm = useCallback(() => {
    void runUpdates(selectedDevices);
  }, [runUpdates, selectedDevices]);

  const handleRetryFailed = useCallback(() => {
    const failed = selectedDevices.filter((d) => itemStates[d.id]?.status === "failed");
    if (failed.length === 0) return;
    void runUpdates(failed);
  }, [itemStates, runUpdates, selectedDevices]);

  const handleClose = useCallback(() => {
    if (phase === "running") return;
    if (phase === "done") {
      const succeededIds = selectedDevices.filter((d) => itemStates[d.id]?.status === "success").map((d) => d.id);
      if (succeededIds.length > 0) onAllSucceeded(succeededIds);
    }
    setPhase("select");
    setSelectedVersion(null);
    setItemStates({});
    onClose();
  }, [itemStates, onAllSucceeded, onClose, phase, selectedDevices]);

  const renderHeader = () => {
    switch (phase) {
      case "select":
        return "AIバージョンを一括更新";
      case "confirm":
        return "更新内容を確認";
      case "running":
        return "更新中…";
      case "done":
        return "更新が完了しました";
    }
  };

  const renderBody = () => {
    if (phase === "select" || phase === "confirm") {
      return (
        <>
          <FormRow>
            <FormLabel>対象デバイス数</FormLabel>
            <FormValue>{selectedDevices.length} 台</FormValue>
          </FormRow>
          <FormRow>
            <FormLabel>更新後のAIバージョン</FormLabel>
            {phase === "select" ? (
              <AimoParkingSelect
                choices={aiVersionChoices}
                value={selectedVersion}
                onChange={setSelectedVersion}
                width="280px"
                placeholder="バージョンを選択"
              />
            ) : (
              <FormValue>{selectedVersion?.label ?? "-"}</FormValue>
            )}
          </FormRow>
          <SummaryLine>選択中のデバイス（{selectedDevices.length} 台）:</SummaryLine>
          <ItemList>
            {selectedDevices.map((d) => (
              <ItemRow key={d.id}>
                <ItemSerial>{d.serialNumber}</ItemSerial>
                <div style={{ color: color.text.gray }}>{d.property?.name ?? "-"}</div>
                <div style={{ color: color.text.gray }}>現在: {d.currentAiSoftware?.version ?? "-"}</div>
              </ItemRow>
            ))}
          </ItemList>
        </>
      );
    }

    return (
      <>
        <SummaryLine>
          {phase === "running"
            ? `${completedCount} / ${totalCount} 件完了`
            : `成功 ${counts.success} 件 / 失敗 ${counts.failed} 件 (合計 ${totalCount} 件)`}
        </SummaryLine>
        <ProgressBarWrap>
          <ProgressBarFill ratio={totalCount === 0 ? 0 : completedCount / totalCount} />
        </ProgressBarWrap>
        <Counts>
          <span>待機中: {counts.pending}</span>
          <span>実行中: {counts.running}</span>
          <span style={{ color: color.green }}>成功: {counts.success}</span>
          <span style={{ color: color.warning }}>失敗: {counts.failed}</span>
        </Counts>
        <ItemList>
          {selectedDevices.map((d) => {
            const state = itemStates[d.id] ?? { status: "pending" as const };
            return (
              <ItemRow key={d.id}>
                <ItemSerial>{d.serialNumber}</ItemSerial>
                <Pill tone={toneForStatus(state.status)}>{labelForStatus(state.status)}</Pill>
                {state.status === "failed" && state.errorMessage && <ItemError>{state.errorMessage}</ItemError>}
              </ItemRow>
            );
          })}
        </ItemList>
      </>
    );
  };

  const renderFooter = () => {
    if (phase === "select") {
      return (
        <>
          <Button label="キャンセル" type="secondary" onClick={handleClose} width="120px" />
          <Button
            label="次へ"
            type="primary"
            onClick={handleStart}
            disabled={selectedVersion == null || selectedDevices.length === 0}
            width="120px"
          />
        </>
      );
    }
    if (phase === "confirm") {
      return (
        <>
          <Button label="戻る" type="secondary" onClick={() => setPhase("select")} width="120px" />
          <Button label={`${selectedDevices.length} 台を更新`} type="primary" onClick={handleConfirm} width="160px" />
        </>
      );
    }
    if (phase === "running") {
      return <Button label="実行中…" type="secondary" onClick={() => undefined} disabled width="120px" />;
    }
    return (
      <>
        {counts.failed > 0 && (
          <Button
            label={`失敗した ${counts.failed} 台を再実行`}
            type="secondary"
            onClick={handleRetryFailed}
            width="200px"
          />
        )}
        <Button label="閉じる" type="primary" onClick={handleClose} width="120px" />
      </>
    );
  };

  return (
    <Modal isVisible={isVisible} onClickOutside={phase === "running" ? () => undefined : handleClose}>
      <Body>
        <Header>{renderHeader()}</Header>
        <Content>{renderBody()}</Content>
        <Footer>{renderFooter()}</Footer>
      </Body>
    </Modal>
  );
};

export default BulkUpdateAiVersionModal;
